import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

/** Dispatched on `window` when the native in-app browser closes (Capacitor only). */
export const CONNECTION_BROWSER_FINISHED_EVENT = 'connection:browser-finished';

/** Dispatched on `window` (via `postMessage`, same origin) when `/api/composio/callback` completes. */
export const CONNECTION_CONNECTED_MESSAGE_TYPE = 'connection:connected';

export type ConnectLaunchMode = 'popup' | 'redirect' | 'native';

/**
 * Opens a Composio OAuth `redirectUrl` using the appropriate surface for the current platform.
 *
 * - Native (Capacitor): opens the system browser (`SFSafariViewController` / Chrome Custom Tabs)
 *   via `@capacitor/browser`, never the app's own WebView. Dispatches
 *   `CONNECTION_BROWSER_FINISHED_EVENT` on `window` when the user closes it, so callers can trigger
 *   an immediate refresh poll.
 * - Web: opens a popup window; falls back to full-page navigation when the popup is blocked.
 *
 * The callback route always completes server-side regardless of which surface hit it, so callers
 * must keep polling as the backstop even when this promise resolves.
 * @param redirectUrl Composio-hosted OAuth consent URL returned by `/api/connections/connect`.
 * @returns Which launch surface was used.
 */
export async function launchConnect(redirectUrl: string): Promise<ConnectLaunchMode> {
  if (Capacitor.isNativePlatform()) {
    const listenerHandle = await Browser.addListener('browserFinished', () => {
      window.dispatchEvent(new Event(CONNECTION_BROWSER_FINISHED_EVENT));
      void listenerHandle.remove();
    });
    await Browser.open({ url: redirectUrl });
    return 'native';
  }

  const popup = window.open(redirectUrl, 'connection-connect', 'popup');
  if (popup) {
    return 'popup';
  }

  window.location.assign(redirectUrl);
  return 'redirect';
}
